import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';

export interface TitleRow {
  id: number;
  charid: number;
  title: string;
  color_decimal: number;
  color_hex: string;
  isactive: number | boolean;
  created_at: string;
  updated_at: string;
}

export interface TagRow {
  id: number;
  charid: number;
  tag: string;
  isactive: number | boolean;
  created_at: string;
  updated_at: string;
}

export interface IconRow {
  ID: number;
  CodeName: string;
  AssocFileIcon128: string;
  icon_url: string;
  owned_left?: boolean;
  owned_right?: boolean;
}

export interface ServiceResponse {
  retry_after?: number;
  success: boolean;
  message: string;
  title_id?: number;
  tag_id?: number;
  active_title_id?: number;
  active_tag_id?: number;
  silk_remaining?: number;
}

export const TM_TITLES_KEY = ['titlemanager', 'titles'] as const;
export const TM_TAGS_KEY = ['titlemanager', 'tags'] as const;
export const TM_ICONS_KEY = (search: string) => ['titlemanager', 'icons', search] as const;
export const TM_USER_ICONS_KEY = ['titlemanager', 'user-icons'] as const;

function invalidateSilk(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['silk-balance'] });
}

export async function reloadTitles(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.refetchQueries({ queryKey: TM_TITLES_KEY });
}

export async function reloadTags(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.refetchQueries({ queryKey: TM_TAGS_KEY });
}

export async function reloadIcons(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.refetchQueries({ queryKey: ['titlemanager', 'icons'] });
  await queryClient.refetchQueries({ queryKey: TM_USER_ICONS_KEY });
}

export function useTitles() {
  return useQuery({
    queryKey: TM_TITLES_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; titles: TitleRow[] }>(
        '/api/v1/titlemanager/titles',
      );
      return data.titles ?? [];
    },
    staleTime: 0,
  });
}

export function useTags() {
  return useQuery({
    queryKey: TM_TAGS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; tags: TagRow[] }>(
        '/api/v1/titlemanager/tags',
      );
      return data.tags ?? [];
    },
    staleTime: 0,
  });
}

export function useIcons(search: string) {
  return useQuery({
    queryKey: TM_ICONS_KEY(search),
    queryFn: async () => {
      const params = search.trim() ? { search: search.trim() } : {};
      const { data } = await api.get<{ success: boolean; icons: IconRow[] }>(
        '/api/v1/titlemanager/icons',
        { params },
      );
      return data.icons ?? [];
    },
    staleTime: 0,
  });
}

export function useUserIcons() {
  return useQuery({
    queryKey: TM_USER_ICONS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{
        success: boolean;
        active_left: IconRow | null;
        active_right: IconRow | null;
      }>('/api/v1/titlemanager/user-icons');
      return {
        activeLeft: data.active_left,
        activeRight: data.active_right,
      };
    },
    staleTime: 0,
  });
}

export function useTitleManagerMutations() {
  const queryClient = useQueryClient();

  const testTitle = useMutation({
    mutationFn: async (payload: { title: string; color: string }) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/test-title', payload);
      return data;
    },
  });

  const buyTitle = useMutation({
    mutationFn: async (payload: { title: string; color: string; buy_color: boolean }) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/buy-title', {
        ...payload,
        buy_color: payload.buy_color ? '1' : '0',
      });
      return data;
    },
    onSuccess: (data) => {
      if (data.success) invalidateSilk(queryClient);
    },
  });

  const editTitle = useMutation({
    mutationFn: async (payload: { title_id: number; title: string; color: string }) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/edit-title', payload);
      return data;
    },
    onSuccess: (data) => {
      if (data.success) invalidateSilk(queryClient);
    },
  });

  const applyTitle = useMutation({
    mutationFn: async (titleId: number) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/apply-title', {
        title_id: titleId,
      });
      return data;
    },
  });

  const removeTitle = useMutation({
    mutationFn: async (titleId: number) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/remove-title', {
        title_id: titleId,
      });
      return data;
    },
  });

  const buyTag = useMutation({
    mutationFn: async (tag: string) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/buy-tag', { tag });
      return data;
    },
    onSuccess: (data) => {
      if (data.success) invalidateSilk(queryClient);
    },
  });

  const applyTag = useMutation({
    mutationFn: async (tagId: number) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/apply-tag', {
        tag_id: tagId,
      });
      return data;
    },
  });

  const removeTag = useMutation({
    mutationFn: async (tagId: number) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/remove-tag', {
        tag_id: tagId,
      });
      return data;
    },
  });

  const buyIcon = useMutation({
    mutationFn: async (payload: { icon_id: number; position: 'left' | 'right' }) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/buy-icon', payload);
      return data;
    },
    onSuccess: (data) => {
      if (data.success) invalidateSilk(queryClient);
    },
  });

  const applyIcon = useMutation({
    mutationFn: async (payload: { icon_id: number; position: 'left' | 'right' }) => {
      const { data } = await api.post<ServiceResponse>('/api/v1/titlemanager/apply-icon', payload);
      return data;
    },
  });

  return {
    testTitle,
    buyTitle,
    editTitle,
    applyTitle,
    removeTitle,
    buyTag,
    applyTag,
    removeTag,
    buyIcon,
    applyIcon,
    reloadTitles: () => reloadTitles(queryClient),
    reloadTags: () => reloadTags(queryClient),
    reloadIcons: () => reloadIcons(queryClient),
  };
}

export function prefetchTitleManager(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.prefetchQuery({
    queryKey: TM_TITLES_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; titles: TitleRow[] }>(
        '/api/v1/titlemanager/titles',
      );
      return data.titles ?? [];
    },
    staleTime: 0,
  });
  void queryClient.prefetchQuery({
    queryKey: TM_TAGS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; tags: TagRow[] }>(
        '/api/v1/titlemanager/tags',
      );
      return data.tags ?? [];
    },
    staleTime: 0,
  });
}
